/**
 * Spalvinu Favourites — client-side favourites set shared across all tiers
 * (guest / demo / premium). Persisted in localStorage so it works for guests
 * who have no account. Stores an array of product handles under one key.
 *
 * Public API on window.SpalvinuFavourites:
 *   get()            -> string[]   list of favourited product handles
 *   has(handle)      -> boolean
 *   add(handle)      -> string[]
 *   remove(handle)   -> string[]
 *   toggle(handle)   -> boolean    new state (true = now favourited)
 *   count()          -> number
 *
 * Dispatches `spalvinu:favourites-changed` on document after every mutation,
 * with detail { handles, count }.
 */
(function () {
  var KEY = "spalvinu:favourites";

  function read() {
    try {
      var stored = localStorage.getItem(KEY);
      var parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (err) {
      return [];
    }
  }

  function write(handles) {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch (err) {}
    document.dispatchEvent(
      new CustomEvent("spalvinu:favourites-changed", {
        detail: { handles: handles, count: handles.length }
      })
    );
    return handles;
  }

  var Favourites = {
    get: function () {
      return read();
    },
    has: function (handle) {
      return read().indexOf(handle) !== -1;
    },
    count: function () {
      return read().length;
    },
    add: function (handle) {
      if (!handle) return read();
      var handles = read();
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        write(handles);
      }
      return handles;
    },
    remove: function (handle) {
      var handles = read().filter(function (h) {
        return h !== handle;
      });
      return write(handles);
    },
    toggle: function (handle) {
      if (!handle) return false;
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    }
  };

  window.SpalvinuFavourites = Favourites;

  // ---- UI wiring ---------------------------------------------------------

  function handleFor(el) {
    if (!el) return "";
    if (el.dataset && el.dataset.productHandle) return el.dataset.productHandle;
    var wrapper = el.closest("[data-product-handle]");
    return wrapper ? wrapper.getAttribute("data-product-handle") : "";
  }

  function syncToggleButtons() {
    var set = read();
    var lookup = {};
    set.forEach(function (h) {
      lookup[h] = true;
    });
    var buttons = document.querySelectorAll("[data-favourite-toggle]");
    Array.prototype.forEach.call(buttons, function (btn) {
      var handle = handleFor(btn);
      var active = !!(handle && lookup[handle]);
      btn.classList.toggle("is-favourited", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function syncOpeners() {
    // Toggle a class on <html> rather than the element's hidden attribute, so
    // the favourites heart's visibility is driven entirely by CSS. The flag is
    // also set pre-paint (layout/theme.liquid) to avoid a layout shift (CLS).
    document.documentElement.classList.toggle("spalvinu-has-favourites", read().length > 0);
  }

  function syncAll() {
    syncToggleButtons();
    syncOpeners();
  }

  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-favourite-toggle]");
    if (!btn) return;
    var handle = handleFor(btn);
    if (!handle) return;
    event.preventDefault();
    var nowFavourited = Favourites.toggle(handle);
    // Pop only when favouriting (not when removing), and only on this click —
    // never on the initial load sync.
    if (nowFavourited) {
      btn.classList.remove("is-fav-bounce");
      void btn.offsetWidth; // reflow so a rapid re-click restarts the animation
      btn.classList.add("is-fav-bounce");
      btn.addEventListener("animationend", function handler() {
        btn.classList.remove("is-fav-bounce");
        btn.removeEventListener("animationend", handler);
      });
    }
  });

  document.addEventListener("spalvinu:favourites-changed", syncAll);

  // Keep tabs in sync if favourites change in another tab/window.
  window.addEventListener("storage", function (event) {
    if (event.key === KEY) syncAll();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAll);
  } else {
    syncAll();
  }
})();
